/**
 * Test Cases Page - Tree View with Folder Hierarchy
 */

import React, { useState, useEffect, useRef } from 'react';
import { apiClient, TestCase, Folder } from '../services/api';
import { useLocation } from 'react-router-dom';
import { FolderTree } from '../components/FolderTree';
import { ColumnSelector, ColumnConfig } from '../components/ColumnSelector';
import { TestCaseImport } from '../components/TestCaseImport';
import { ProjectFolderStats } from '../components/ProjectFolderStats';
import { useRBAC } from '../components/ProtectedRoute';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { AIGenerateModal } from '../components/AIGenerateModal';
import { ReviewModal } from '../components/ReviewModal';
import { ViewDetailModal, priorityBadge } from '../components/ViewDetailModal';
import { BulkEditModal, BulkEditPayload } from '../components/BulkEditModal';
import { FilterPanel } from '../components/FilterPanel';
import { RichTextEditor, RichTextDisplay, stripHtml } from '../components/RichTextEditor';
import { useAuth } from '../contexts/AuthContext';
import { ProductTour } from '../components/ProductTour';
import { useProductTour, isTourSeen, TOUR_IDS } from '../hooks/useProductTour';
import { testCasesTourSteps, foldersTourSteps } from '../tours/tourSteps';
import { parseJiraTickets } from '../utils/formatters';
import './TestCasesPage.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

type Priority = 'Critical' | 'High' | 'Medium' | 'Low';

const PREDEFINED_TAGS = [
  'Regression', 'Smoke', 'Sanity', 'Integration',
  'API', 'UI', 'Performance', 'Security', 'Functional', 'AI Generated'
];

interface TestCaseFormData {
  test_case_description: string;
  preconditions: string;
  priority: Priority;
  steps: string;
  expected_result: string;
  tags: string[];
}

interface FolderFormData {
  name: string;
  description: string;
}

export const TestCasesPage: React.FC = () => {
  const { canWrite } = useRBAC();
  const location = useLocation();
  const { confirm, dialog } = useConfirmDialog();
  const [showAIModal, setShowAIModal] = useState(false);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  // A "leaf" (non-root) folder must be selected for most tour steps' targets
  // (Create Test Case, Import, Bulk Edit, Export, Share Link, Create Test Run) to exist —
  // those buttons only render for non-project folders. Auto-start waits for that; the
  // manual trigger auto-selects one so "Take a Tour" never breaks mid-way.
  const selectedIsLeafFolder = !!selectedFolderId && folders.find(f => f.id === selectedFolderId)?.level !== 0;
  const ensureLeafFolderSelected = () => {
    if (selectedIsLeafFolder) return;
    const leaf = folders.find(f => f.level !== 0);
    if (leaf) setSelectedFolderId(leaf.id);
  };
  const { run: tourRun, start: startTour, handleCallback: tourCallback } = useProductTour(TOUR_IDS.TEST_CASES, true, selectedIsLeafFolder);
  const { run: folderTourRun, start: startFolderTour, handleCallback: folderTourCallback } = useProductTour(TOUR_IDS.FOLDERS, false);

  // First-ever visit: nothing is selected by default, so the auto-play tour would wait
  // forever for a leaf folder. Proactively select one (once) so auto-play can actually fire.
  const autoTourFolderAttempted = useRef(false);
  useEffect(() => {
    if (autoTourFolderAttempted.current) return;
    if (isTourSeen(TOUR_IDS.TEST_CASES)) return;
    if (selectedFolderId || folders.length === 0) return;
    const leaf = folders.find(f => f.level !== 0);
    if (leaf) {
      autoTourFolderAttempted.current = true;
      setSelectedFolderId(leaf.id);
    }
  }, [folders, selectedFolderId]);

  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [sortField, setSortField] = useState<'id' | 'priority' | ''>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('tc-sidebar-open');
    return saved === null ? true : saved === 'true';
  });
  
  const [showTestCaseForm, setShowTestCaseForm] = useState(false);
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [showRenameForm, setShowRenameForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [reviewFolderId, setReviewFolderId] = useState<string | null>(null);
  const [reviewMode, setReviewMode] = useState<'submit' | 'review' | 'view'>('submit');
  const [allUsers, setAllUsers] = useState<{ email: string; name: string }[]>([]);
  const { user: currentUser } = useAuth();
  const [viewingTestCase, setViewingTestCase] = useState<any | null>(null);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState<TestCase | null>(null);
  const [folderFormParentId, setFolderFormParentId] = useState<string | null>(null);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [selectedTestCases, setSelectedTestCases] = useState<Set<string>>(new Set());
  
  const [testCaseFormData, setTestCaseFormData] = useState<TestCaseFormData>({
    test_case_description: '',
    preconditions: '',
    priority: 'Medium',
    steps: '',
    expected_result: '',
    tags: []
  });
  
  const [folderFormData, setFolderFormData] = useState<FolderFormData>({
    name: '',
    description: ''
  });
  
  const [tagInput, setTagInput] = useState('');
  const tagInputRef = useRef<HTMLInputElement>(null);
  
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'id', label: 'ID', visible: true, required: true },
    { key: 'description', label: 'Description', visible: true, required: true },
    { key: 'priority', label: 'Priority', visible: true },
    { key: 'tags', label: 'Tags', visible: false },
    { key: 'preconditions', label: 'Preconditions', visible: false },
    { key: 'steps', label: 'Steps', visible: false },
    { key: 'expected', label: 'Expected Result', visible: true },
    { key: 'created_date', label: 'Created Date', visible: true },
    { key: 'created_by', label: 'Created By', visible: false },
    { key: 'actions', label: 'Actions', visible: true, required: true }
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sharedFolderId = params.get('folder_id');
    if (folders.length > 0 && sharedFolderId) {
      setSelectedFolderId(sharedFolderId);
    } else {
      loadFolders(sharedFolderId || undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedFolderId) {
      loadTestCases();
    } else {
      setTestCases([]);
    }
  }, [selectedFolderId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const saved = localStorage.getItem('test-cases-columns');
    if (saved) {
      try {
        const visibilityMap = JSON.parse(saved);
        setColumns(cols => cols.map(col => ({
          ...col,
          visible: visibilityMap[col.key] ?? col.visible
        })));
      } catch (e) {
        console.error('Failed to load column preferences:', e);
      }
    }
  }, []);

  const loadFolders = async (autoSelectId?: string) => {
    try {
      const data = await apiClient.getFolders('test_case');
      setFolders(data);
      if (autoSelectId) {
        setSelectedFolderId(autoSelectId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folders');
    }
  };

  const loadTestCases = async () => {
    if (!selectedFolderId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.getTestCases(selectedFolderId);
      setTestCases(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load test cases');
    } finally {
      setIsLoading(false);
    }
  };

  const getBreadcrumb = (): Folder[] => {
    if (!selectedFolderId) return [];
    const path: Folder[] = [];
    // eslint-disable-next-line no-loop-func
    let current = folders.find(f => f.id === selectedFolderId);
    while (current) {
      path.unshift(current);
      // eslint-disable-next-line no-loop-func
      current = current.parent_id ? folders.find(f => f.id === current!.parent_id) : undefined;
    }
    return path;
  };

  const handleCreateTestCase = () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    setEditingTestCase(null);
    setTestCaseFormData({
      test_case_description: '',
      preconditions: '',
      priority: 'Medium',
      steps: '',
      expected_result: '',
      tags: []
    });
    setTagInput('');
    setShowTestCaseForm(true);
  };

  const handleEditTestCase = (testCase: TestCase) => {
    setEditingTestCase(testCase);
    setTestCaseFormData({
      test_case_description: testCase.test_case_description,
      preconditions: testCase.preconditions || '',
      priority: testCase.priority,
      steps: testCase.steps || '',
      expected_result: testCase.expected_result,
      tags: testCase.tags || []
    });
    setTagInput('');
    setShowTestCaseForm(true);
  };

  const handleSubmitTestCase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFolderId) return;
    setIsLoading(true);
    setError(null);
    try {
      if (editingTestCase) {
        await apiClient.updateTestCase(editingTestCase.id, testCaseFormData);
      } else {
        await apiClient.createTestCaseManually({
          folder_id: selectedFolderId,
          ...testCaseFormData
        });
      }
      setShowTestCaseForm(false);
      setEditingTestCase(null);
      await loadTestCases();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save test case');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTestCase = async (caseId: string) => {
    const confirmed = await confirm('Delete Test Case', 'Are you sure you want to delete this test case? This action cannot be undone.');
    if (!confirmed) return;
    try {
      await apiClient.deleteTestCase(caseId);
      await loadTestCases();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete test case');
    }
  };

  const handleBulkDeleteTestCases = async () => {
    if (selectedTestCases.size === 0) {
      setError('Please select at least one test case to delete');
      return;
    }

    const count = selectedTestCases.size;
    const confirmed = await confirm('Delete Test Cases', `Are you sure you want to delete ${count} selected test case(s)? This action cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const caseIds = Array.from(selectedTestCases);
      const result = await apiClient.bulkDeleteTestCases(caseIds);
      
      if (result.failed_ids.length > 0) {
        setError(`Deleted ${result.deleted_count} of ${result.total_requested} test cases. ${result.failed_ids.length} failed.`);
      }
      
      setSelectedTestCases(new Set()); // Clear selection
      await loadTestCases();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete test cases');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkEditTestCases = async (payload: BulkEditPayload) => {
    const ids = Array.from(selectedTestCases);
    const result = await apiClient.bulkUpdateTestCases({ case_ids: ids, ...payload });
    if (result.failed_ids.length > 0) {
      setError(`Updated ${result.updated_count} of ${result.total_requested} test cases. ${result.failed_ids.length} failed.`);
    }
    setShowBulkEditModal(false);
    setSelectedTestCases(new Set());
    await loadTestCases();
  };

  const handleCreateSubfolder = (parentId: string) => {
    setFolderFormParentId(parentId);
    setFolderFormData({ name: '', description: '' });
    setShowFolderForm(true);
  };

  const handleSubmitFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.createFolder({
        name: folderFormData.name,
        description: folderFormData.description,
        parent_id: folderFormParentId || undefined,
        folder_type: 'test_case'
      });
      setShowFolderForm(false);
      setFolderFormParentId(null);
      await loadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenameFolder = (folderId: string) => {
    const folder = folders.find(f => f.id === folderId);
    if (folder) {
      setRenamingFolderId(folderId);
      setFolderFormData({ name: folder.name, description: folder.description || '' });
      setShowRenameForm(true);
    }
  };

  const handleSubmitRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!renamingFolderId) return;
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.updateFolder(renamingFolderId, {
        name: folderFormData.name,
        description: folderFormData.description
      });
      setShowRenameForm(false);
      setRenamingFolderId(null);
      await loadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    const folder = folders.find(f => f.id === folderId);
    if (!folder) return;
    const hasChildren = folders.some(f => f.parent_id === folderId);
    const message = hasChildren
      ? `Delete "${folder.name}" and all its subfolders and test cases? This cannot be undone.`
      : `Delete "${folder.name}" and all its test cases? This cannot be undone.`;
    const confirmed = await confirm('Delete Folder', message);
    if (!confirmed) return;
    try {
      await apiClient.deleteFolder(folderId);
      if (selectedFolderId === folderId) {
        setSelectedFolderId(null);
      }
      await loadFolders();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to delete folder';
      setError(errorMessage);
    }
  };

  const handleMoveFolder = async (folderId: string, newParentId: string | null) => {
    try {
      await apiClient.moveFolder(folderId, newParentId);
      await loadFolders();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to move folder');
    }
  };

  const handleSubmitReview = async (folderId: string) => {
    if (allUsers.length === 0) {
      try {
        const users = await apiClient.getAllUsers();
        setAllUsers(users.map((u: any) => ({ email: u.email, name: u.name })));
      } catch {}
    }
    setReviewFolderId(folderId);
    setReviewMode('submit');
  };

  const handleReviewAction = async (folderId: string) => {
    setReviewFolderId(folderId);
    setReviewMode('review');
  };

  const handleToggleTestCase = (testCaseId: string) => {
    const newSelected = new Set(selectedTestCases);
    if (newSelected.has(testCaseId)) {
      newSelected.delete(testCaseId);
    } else {
      newSelected.add(testCaseId);
    }
    setSelectedTestCases(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTestCases.size === filteredTestCases.length) {
      setSelectedTestCases(new Set());
    } else {
      setSelectedTestCases(new Set(filteredTestCases.map(tc => tc.id)));
    }
  };

  const handleImportExcel = () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    setShowImportModal(true);
  };

  const handleImportComplete = async (_importedTestCases: TestCase[]) => {
    setShowImportModal(false);
    // Reload folders (XML import creates subfolders) then reload test cases
    const currentFolderId = selectedFolderId;
    try {
      const updatedFolders = await apiClient.getFolders('test_case');
      setFolders(updatedFolders);
    } catch (e) { /* ignore */ }
    // Explicitly reload test cases for the current folder
    if (currentFolderId) {
      setIsLoading(true);
      setError(null);
      try {
        const data = await apiClient.getTestCases(currentFolderId);
        setTestCases(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load test cases');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleExportTestCases = async () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    
    if (selectedTestCases.size === 0) {
      setError('Please select at least one test case to export');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Get token from auth_tokens (same as api.ts)
      const authTokens = localStorage.getItem('auth_tokens');
      if (!authTokens) {
        throw new Error('Not authenticated. Please log in again.');
      }
      
      const tokens = JSON.parse(authTokens);
      const accessToken = tokens.access_token;
      
      // Use POST endpoint with proper structure
      const response = await fetch(`${API_BASE_URL}/api/v1/export/test-cases`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          format: 'excel',
          scope: 'test_cases',
          folder_id: selectedFolderId
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Export failed: ${response.status} - ${errorText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `test_cases_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSelectedTestCases(new Set()); // Clear selection after export
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export test cases');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareLink = async () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    try {
      // Build human-readable path from breadcrumb
      const path = getBreadcrumb().map(f => encodeURIComponent(f.name)).join('/');
      const link = `${window.location.origin}/test-cases/${path}?folder_id=${selectedFolderId}`;
      setShareLink(link);
      setShowShareModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get share link');
    }
  };

  const handleCreateTestRun = () => {
    if (!selectedFolderId) {
      setError('Please select a folder first');
      return;
    }
    window.location.href = `/test-runs/create?folder_id=${selectedFolderId}&type=test_case`;
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !testCaseFormData.tags.includes(tag)) {
      setTestCaseFormData({ ...testCaseFormData, tags: [...testCaseFormData.tags, tag] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTestCaseFormData({
      ...testCaseFormData,
      tags: testCaseFormData.tags.filter(t => t !== tagToRemove)
    });
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const TC_PRIORITY_ORDER: Record<string, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };

  const filteredTestCases = (() => {
    let list = searchTerm
      ? testCases.filter(tc =>
          tc.test_case_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          stripHtml(tc.test_case_description).toLowerCase().includes(searchTerm.toLowerCase()) ||
          (tc.expected_result && stripHtml(tc.expected_result).toLowerCase().includes(searchTerm.toLowerCase())) ||
          (tc.tags && tc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
        )
      : [...testCases];

    if (filterPriority) list = list.filter(tc => tc.priority === filterPriority);
    if (filterTag) list = list.filter(tc => tc.tags && tc.tags.includes(filterTag));

    if (sortField === 'id') {
      list.sort((a, b) => {
        const na = parseInt(a.test_case_id.replace(/\D/g, '')) || 0;
        const nb = parseInt(b.test_case_id.replace(/\D/g, '')) || 0;
        return sortDir === 'asc' ? na - nb : nb - na;
      });
    } else if (sortField === 'priority') {
      list.sort((a, b) => {
        const pa = TC_PRIORITY_ORDER[a.priority] ?? 99;
        const pb = TC_PRIORITY_ORDER[b.priority] ?? 99;
        return sortDir === 'asc' ? pa - pb : pb - pa;
      });
    }
    return list;
  })();

  const handleSort = (field: 'id' | 'priority') => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sortIcon = (field: 'id' | 'priority') => {
    if (sortField !== field) return <span style={{ color: '#d1d5db', marginLeft: 4 }}>↕</span>;
    return <span style={{ color: '#1e3a8a', marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const toggleSidebar = () => {
    setSidebarOpen(prev => {
      localStorage.setItem('tc-sidebar-open', String(!prev));
      return !prev;
    });
  };

  const breadcrumb = getBreadcrumb();

  return (
    <div className="test-cases-page-tree">
      <ProductTour steps={testCasesTourSteps} run={tourRun} onCallback={tourCallback} />
      <ProductTour steps={foldersTourSteps} run={folderTourRun} onCallback={folderTourCallback} />
      {dialog}
      {error && (
        <div className="error-banner">
          {typeof error === 'string' ? error : JSON.stringify(error)}
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}
      <div className="test-cases-layout">
        <div className={`test-cases-sidebar${sidebarOpen ? '' : ' sidebar-collapsed'}`}>
          <div className="sidebar-inner" data-tour="testcase-folder-tree">
            <FolderTree
            folders={folders}
            selectedFolderId={selectedFolderId}
            onSelectFolder={setSelectedFolderId}
            onCreateSubfolder={handleCreateSubfolder}
            onRenameFolder={handleRenameFolder}
            onDeleteFolder={handleDeleteFolder}
            onMoveFolder={handleMoveFolder}
            onSubmitReview={handleSubmitReview}
            onReviewAction={handleReviewAction}
            currentUserEmail={currentUser?.email}
            title="Test Cases"
          />
          </div>
          <button className="sidebar-toggle-btn" onClick={toggleSidebar} title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              {sidebarOpen ? <path d="M15 18l-6-6 6-6"/> : <path d="M9 18l6-6-6-6"/>}
            </svg>
          </button>
        </div>
        <div className="test-cases-content">
          {selectedFolderId ? (
            <>
              <div className="content-breadcrumb">
                <span className="breadcrumb-icon">📍</span>
                {breadcrumb.map((folder, index) => (
                  <React.Fragment key={folder.id}>
                    {index > 0 && <span className="breadcrumb-separator">›</span>}
                    <span className="breadcrumb-item">
                      {parseJiraTickets(folder.name).map((seg, i) =>
                        typeof seg === 'string'
                          ? <React.Fragment key={i}>{seg}</React.Fragment>
                          : <a
                              key={i}
                              href={seg.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="breadcrumb-jira-link"
                              onClick={e => e.stopPropagation()}
                              title={`Open ${seg.ticket} in Jira`}
                            >{seg.ticket}</a>
                      )}
                    </span>
                  </React.Fragment>
                ))}
                {(() => {
                  const sf = folders.find(f => f.id === selectedFolderId);
                  const status = sf?.review_status || 'Draft';
                  const cfg: Record<string, { bg: string; icon: React.ReactNode; label: string }> = {
                    'Draft': {
                      bg: '#6b7280',
                      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
                      label: 'Draft'
                    },
                    'In Review': {
                      bg: '#29b6f6',
                      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
                      label: 'In Review'
                    },
                    'Approved': {
                      bg: '#2ecc71',
                      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="0"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5l-4.5-4.5 1.41-1.41L10 13.67l7.09-7.09 1.41 1.41L10 16.5z"/></svg>,
                      label: 'Approved'
                    },
                    'Changes Requested': {
                      bg: '#f05a6e',
                      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>,
                      label: 'Change Requested'
                    },
                  };
                  const c = cfg[status] || cfg['Draft'];
                  const pillF = folders.find(f => f.id === selectedFolderId);
                  const canOpenReview = status !== 'Draft' && pillF;
                  const handlePillClick = () => {
                    if (!pillF || !canOpenReview) return;
                    if (status === 'In Review' && (pillF.reviewer_email === currentUser?.email || currentUser?.role === 'Admin')) {
                      // Assigned reviewer OR Admin can take review action
                      handleReviewAction(pillF.id);
                    } else if (status === 'In Review') {
                      setReviewFolderId(pillF.id);
                      setReviewMode('view');
                    } else if (status === 'Approved') {
                      setReviewFolderId(pillF.id);
                      setReviewMode('view');
                    } else if (status === 'Changes Requested' && pillF.submitted_by === currentUser?.email) {
                      setReviewFolderId(pillF.id);
                      setReviewMode('submit');
                    }
                  };
                  return (
                    <span
                      className="review-status-pill"
                      style={{ background: c.bg, cursor: canOpenReview ? 'pointer' : 'default' }}
                      onClick={handlePillClick}
                      title={canOpenReview ? 'Click to view review details' : ''}
                    >
                      {c.icon}{c.label}
                    </span>
                  );
                })()}
              </div>
              <div className="content-actions">
                {canWrite && (
                  <button className="btn btn-secondary" data-tour="create-subfolder-btn" onClick={() => handleCreateSubfolder(selectedFolderId)}>
                    <span>📁</span> Create Subfolder
                  </button>
                )}
                {canWrite && folders.find(f => f.id === selectedFolderId)?.level !== 0 && (
                  <button className="btn btn-primary" data-tour="create-testcase-btn" onClick={handleCreateTestCase}>
                    <span>➕</span> Create Test Case
                  </button>
                )}
                {canWrite && folders.find(f => f.id === selectedFolderId)?.level !== 0 && (
                  <button className="btn btn-ai" onClick={() => setShowAIModal(true)}>
                    ✨ Generate with AI
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => { ensureLeafFolderSelected(); startFolderTour(); }}
                  title="Learn about folder management: subfolders, rename, move, review, share links"
                >
                  🗂️ Folder Tips
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => { ensureLeafFolderSelected(); startTour(); }}
                  title="Replay the guided tour for this page"
                >
                  🎯 Take a Tour
                </button>
              </div>

              {/* Action bar — Import always visible; rest only for non-root folders */}
              {(() => {
                const isProjectFolder = folders.find(f => f.id === selectedFolderId)?.level === 0;
                return (
                  <div className="action-bar">
                    {canWrite && (
                      <button className="btn btn-action" data-tour="import-btn" onClick={handleImportExcel} title="Import test cases from Excel or XML">
                        <span>📥</span> Import
                      </button>
                    )}
                    {!isProjectFolder && canWrite && (
                      <>
                        <button
                          className="btn btn-action"
                          data-tour="export-btn"
                          onClick={handleExportTestCases}
                          disabled={selectedTestCases.size === 0}
                          title={selectedTestCases.size === 0 ? "Select test cases to export" : `Export ${selectedTestCases.size} selected test case(s)`}
                        >
                          <span>📤</span> Export ({selectedTestCases.size})
                        </button>
                        <button
                          className="btn btn-action btn-delete"
                          onClick={handleBulkDeleteTestCases}
                          disabled={selectedTestCases.size === 0}
                          title={selectedTestCases.size === 0 ? "Select test cases to delete" : `Delete ${selectedTestCases.size} selected test case(s)`}
                        >
                          <span>🗑️</span> Delete Selected ({selectedTestCases.size})
                        </button>
                        <button
                          className="btn btn-action"
                          data-tour="bulk-edit-btn"
                          onClick={() => setShowBulkEditModal(true)}
                          disabled={selectedTestCases.size < 2}
                          title={selectedTestCases.size < 2 ? "Select 2+ test cases to bulk edit" : `Bulk edit ${selectedTestCases.size} test cases`}
                        >
                          <span>✏️</span> Bulk Edit ({selectedTestCases.size})
                        </button>
                        <button className="btn btn-action" data-tour="share-link-btn" onClick={handleShareLink} title="Get shareable link for this folder">
                          <span>🔗</span> Share Link
                        </button>
                        {(() => {
                          const sf = folders.find(f => f.id === selectedFolderId);
                          const approved = sf?.review_status === 'Approved';
                          return (
                            <button
                              className="btn btn-action"
                              data-tour="create-test-run-btn"
                              onClick={approved ? handleCreateTestRun : undefined}
                              disabled={!approved}
                              title={approved ? 'Create test run from test cases' : `Folder is "${sf?.review_status || 'Draft'}" — must be Approved to create a Test Run`}
                              style={!approved ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
                            >
                              <span>▶️</span> Create Test Run
                            </button>
                          );
                        })()}
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Root folder: stats only. Sub-folders: full table */}
              {(() => {
                const isProjectFolder = folders.find(f => f.id === selectedFolderId)?.level === 0;

                if (isProjectFolder) {
                  return <ProjectFolderStats folderId={selectedFolderId} folderType="test_case" />;
                }

                return (
                  <>
                    <div className="content-controls">
                      <input
                        type="text"
                        className="search-input search-input-sm"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />

                      <FilterPanel
                        filters={[
                          { key: 'priority', label: 'Priority', options: ['Critical','High','Medium','Low'], value: filterPriority, onChange: setFilterPriority },
                          { key: 'tag', label: 'Tag', options: ['Smoke','Sanity','Integration','API','UI','Performance','Security','Functional','Regression','AI Generated'], value: filterTag, onChange: setFilterTag },
                        ]}
                        onClearAll={() => { setFilterPriority(''); setFilterTag(''); }}
                      />

                      <ColumnSelector columns={columns} onColumnsChange={setColumns} storageKey="test-cases-columns" />
                      <div className="stats-badge">Total: <strong>{testCases.length}</strong></div>
                      {(searchTerm || filterPriority || filterTag) && filteredTestCases.length !== testCases.length && (
                        <div className="stats-badge filtered">Filtered: <strong>{filteredTestCases.length}</strong></div>
                      )}
                    </div>
                    {isLoading ? (
                      <div className="content-loading">Loading test cases...</div>
                    ) : filteredTestCases.length === 0 ? (
                      <div className="content-empty">
                        <p>{searchTerm ? 'No test cases match your search' : 'No test cases in this folder'}</p>
                        <button className="btn btn-primary" onClick={handleCreateTestCase}>
                          Create First Test Case
                        </button>
                      </div>
                    ) : (
                      <div className="test-cases-table-container">
                        <table className="test-cases-table">
                          <thead>
                            <tr>
                              <th className="col-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedTestCases.size === filteredTestCases.length && filteredTestCases.length > 0}
                                  onChange={handleSelectAll}
                                  title="Select all"
                                />
                              </th>
                              {columns.filter(col => col.visible).map(col => (
                                <th key={col.key} className={`col-${col.key}`}
                                  style={(col.key === 'id' || col.key === 'priority') ? { cursor: 'pointer', userSelect: 'none' } : {}}
                                  onClick={(col.key === 'id' || col.key === 'priority') ? () => handleSort(col.key as 'id' | 'priority') : undefined}
                                >
                                  {col.label}
                                  {(col.key === 'id' || col.key === 'priority') && sortIcon(col.key as 'id' | 'priority')}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredTestCases.map(testCase => (
                              <tr key={testCase.id} className={selectedTestCases.has(testCase.id) ? 'selected-row' : ''}>
                                <td className="col-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={selectedTestCases.has(testCase.id)}
                                    onChange={() => handleToggleTestCase(testCase.id)}
                                  />
                                </td>
                                {columns.find(c => c.key === 'id')?.visible && (
                                  <td className="col-id">
                                    <span
                                      className="test-case-id"
                                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                      onClick={() => setViewingTestCase(testCase)}
                                    >{testCase.test_case_id}</span>
                                  </td>
                                )}
                                {columns.find(c => c.key === 'description')?.visible && (
                                  <td className="col-description">
                                    <strong><RichTextDisplay html={testCase.test_case_description} /></strong>
                                  </td>
                                )}
                                {columns.find(c => c.key === 'priority')?.visible && (
                                  <td className="col-priority">
                                    <span className={`priority-badge priority-${testCase.priority.toLowerCase()}`}>
                                      {testCase.priority}
                                    </span>
                                  </td>
                                )}
                                {columns.find(c => c.key === 'tags')?.visible && (
                                  <td className="col-tags">
                                    {testCase.tags && testCase.tags.length > 0 ? (
                                      <div className="tags-cell">
                                        {testCase.tags.map(tag => (
                                          <span key={tag} className="tag-badge">{tag}</span>
                                        ))}
                                      </div>
                                    ) : <span className="no-data">—</span>}
                                  </td>
                                )}
                                {columns.find(c => c.key === 'preconditions')?.visible && (
                                  <td className="col-preconditions">
                                    {testCase.preconditions || <span className="no-data">—</span>}
                                  </td>
                                )}
                                {columns.find(c => c.key === 'steps')?.visible && (
                                  <td className="col-steps">
                                    {testCase.steps ? <RichTextDisplay html={testCase.steps} className="steps-cell" /> : <span className="no-data">—</span>}
                                  </td>
                                )}
                                {columns.find(c => c.key === 'expected')?.visible && (
                                  <td className="col-expected">
                                    <div className="expected-cell">
                                      {testCase.expected_result
                                        ? <RichTextDisplay html={testCase.expected_result} />
                                        : <span className="no-data">—</span>
                                      }
                                    </div>
                                  </td>
                                )}
                                {columns.find(c => c.key === 'created_date')?.visible && (
                                  <td className="col-created-date">
                                    {new Date(testCase.created_at).toLocaleDateString()}
                                  </td>
                                )}
                                {columns.find(c => c.key === 'created_by')?.visible && (
                                  <td className="col-created-by">{testCase.created_by}</td>
                                )}
                                {columns.find(c => c.key === 'actions')?.visible && (
                                  <td className="col-actions">
                                    <div className="action-buttons">
                                      {canWrite && <button className="action-btn edit" onClick={() => handleEditTestCase(testCase)} title="Edit">✏️</button>}
                                      {canWrite && <button className="action-btn delete" onClick={() => handleDeleteTestCase(testCase.id)} title="Delete">🗑️</button>}
                                      {!canWrite && <span style={{ color: '#d1d5db', fontSize: '12px' }}>—</span>}
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          ) : (
            <div className="content-empty-state">
              <div className="empty-state-icon">📁</div>
              <h3>Select a Folder</h3>
              <p>Choose a folder from the tree to view and manage test cases</p>
              <button
                className="btn btn-secondary"
                style={{ marginTop: '16px' }}
                onClick={() => { ensureLeafFolderSelected(); startTour(); }}
                title="See a guided walkthrough of this page"
              >
                🎯 Take a Tour
              </button>
            </div>
          )}
        </div>
      </div>
      {showTestCaseForm && (
        <div className="tc-modal-overlay" onClick={() => setShowTestCaseForm(false)}>
          <div className="tc-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="tc-modal-header">
              <div>
                <h2 className="tc-modal-title">{editingTestCase ? 'Edit Test Case' : 'Create Test Case'}</h2>
                <p className="tc-modal-subtitle">Add a new manual test case to the current folder.</p>
              </div>
              <button className="tc-modal-close" onClick={() => setShowTestCaseForm(false)} title="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Scrollable body */}
            <form onSubmit={handleSubmitTestCase} className="tc-modal-body">
              {/* Description */}
              <div className="tc-field">
                <label className="tc-label">Description <span className="tc-required">*</span></label>
                <RichTextEditor
                  value={testCaseFormData.test_case_description}
                  onChange={(val) => setTestCaseFormData({ ...testCaseFormData, test_case_description: val })}
                  placeholder="e.g., Verify successful login with valid credentials"
                  minHeight={60}
                  withCode
                />
              </div>

              {/* Priority + Tags row */}
              <div className="tc-row">
                <div className="tc-field">
                  <label className="tc-label">Priority <span className="tc-required">*</span></label>
                  <div className="tc-select-wrap">
                    <select
                      className="tc-select"
                      value={testCaseFormData.priority}
                      onChange={(e) => setTestCaseFormData({ ...testCaseFormData, priority: e.target.value as Priority })}
                      required
                    >
                      <option value="Critical">Critical</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                    <svg className="tc-select-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                  </div>
                </div>

                <div className="tc-field tc-field-full">
                  <div className="tc-tags-header">
                    <label className="tc-label">Classification Tags</label>
                    <span className="tc-tags-hint">SELECT MULTIPLE</span>
                  </div>
                  <div className="tc-tags-pills">
                    {PREDEFINED_TAGS.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        className={`tc-tag-pill${testCaseFormData.tags.includes(tag) ? ' tc-tag-pill-active' : ''}`}
                        onClick={() => {
                          const already = testCaseFormData.tags.includes(tag);
                          setTestCaseFormData({
                            ...testCaseFormData,
                            tags: already
                              ? testCaseFormData.tags.filter(t => t !== tag)
                              : [...testCaseFormData.tags, tag]
                          });
                        }}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                  {/* Custom tag input */}
                  <div className="tc-tags-custom">
                    <div className="tc-tags-input-container">
                      <input
                        ref={tagInputRef}
                        type="text"
                        className="tc-tags-inline-input"
                        value={tagInput}
                        onChange={(e) => { setTagInput(e.target.value); }}
                        onKeyDown={handleTagInputKeyDown}
                        placeholder="+ Custom tag (press Enter)"
                      />
                    </div>
                    {testCaseFormData.tags.filter(t => !PREDEFINED_TAGS.includes(t)).length > 0 && (
                      <div className="tc-custom-chips">
                        {testCaseFormData.tags.filter(t => !PREDEFINED_TAGS.includes(t)).map(tag => (
                          <span key={tag} className="tc-tag-chip">
                            {tag}
                            <button type="button" className="tc-tag-remove" onClick={() => handleRemoveTag(tag)}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Preconditions */}
              <div className="tc-field">
                <label className="tc-label">Preconditions</label>
                <textarea
                  className="tc-textarea"
                  rows={2}
                  placeholder="List initial state requirements..."
                  value={testCaseFormData.preconditions}
                  onChange={(e) => setTestCaseFormData({ ...testCaseFormData, preconditions: e.target.value })}
                />
              </div>

              {/* Steps */}
              <div className="tc-field">
                <label className="tc-label">Steps</label>
                <RichTextEditor
                  value={testCaseFormData.steps}
                  onChange={(val) => setTestCaseFormData({ ...testCaseFormData, steps: val })}
                  placeholder="1. Navigate to /login&#10;2. Enter username&#10;3. Enter password"
                  minHeight={100}
                  withCode
                />
              </div>

              {/* Expected Result */}
              <div className="tc-field">
                <label className="tc-label">Expected Result <span className="tc-required">*</span></label>
                <RichTextEditor
                  value={testCaseFormData.expected_result}
                  onChange={(val) => setTestCaseFormData({ ...testCaseFormData, expected_result: val })}
                  placeholder="User is redirected to dashboard with a welcome message"
                  minHeight={80}
                  withCode
                />
              </div>

              {/* Footer inside form */}
              <div className="tc-modal-footer">
                <button type="button" className="tc-btn-cancel" onClick={() => setShowTestCaseForm(false)}>Cancel</button>
                <button type="submit" className="tc-btn-submit" disabled={isLoading}>
                  {isLoading ? 'Saving...' : editingTestCase ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showFolderForm && (
        <div className="modal-overlay" onClick={() => setShowFolderForm(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Subfolder</h3>
              <button onClick={() => setShowFolderForm(false)} className="modal-close">×</button>
            </div>
            <form onSubmit={handleSubmitFolder} className="modal-form">
              <div className="form-group">
                <label>Folder Name <span className="required">*</span></label>
                <input type="text" className="form-control" value={folderFormData.name}
                  onChange={(e) => setFolderFormData({ ...folderFormData, name: e.target.value })} required autoFocus />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea className="form-control" value={folderFormData.description}
                  onChange={(e) => setFolderFormData({ ...folderFormData, description: e.target.value })} rows={2} />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Creating...' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowFolderForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showRenameForm && (
        <div className="modal-overlay" onClick={() => setShowRenameForm(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Rename Folder</h3>
              <button onClick={() => setShowRenameForm(false)} className="modal-close">×</button>
            </div>
            <form onSubmit={handleSubmitRename} className="modal-form">
              <div className="form-group">
                <label>Folder Name <span className="required">*</span></label>
                <input type="text" className="form-control" value={folderFormData.name}
                  onChange={(e) => setFolderFormData({ ...folderFormData, name: e.target.value })} required autoFocus />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea className="form-control" value={folderFormData.description}
                  onChange={(e) => setFolderFormData({ ...folderFormData, description: e.target.value })} rows={2} />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowRenameForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Link Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Share Folder Link</h3>
              <button onClick={() => setShowShareModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <p>Share this link to give others access to this folder:</p>
              <div className="share-link-container">
                <input 
                  type="text" 
                  className="form-control" 
                  value={shareLink} 
                  readOnly 
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    navigator.clipboard.writeText(shareLink);
                    alert('Link copied to clipboard!');
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowShareModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showAIModal && selectedFolderId && (
        <AIGenerateModal
          folderId={selectedFolderId}
          type="test_case"
          onClose={() => setShowAIModal(false)}
          onSaved={() => { setShowAIModal(false); loadTestCases(); }}
        />
      )}

      {showImportModal && selectedFolderId && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <TestCaseImport
              folderId={selectedFolderId}
              onImportComplete={handleImportComplete}
              onCancel={() => setShowImportModal(false)}
            />
          </div>
        </div>
      )}

      {reviewFolderId && (() => {
        const reviewFolder = folders.find(f => f.id === reviewFolderId);
        return reviewFolder ? (
          <ReviewModal
            folder={reviewFolder}
            allUsers={allUsers}
            mode={reviewMode}
            onClose={() => setReviewFolderId(null)}
            onDone={async () => { setReviewFolderId(null); await loadFolders(); }}
          />
        ) : null;
      })()}

      {viewingTestCase && (
        <ViewDetailModal
          title={viewingTestCase.test_case_id}
          subtitle={viewingTestCase.test_case_description}
          fields={[
            { label: 'Priority', value: priorityBadge(viewingTestCase.priority) },
            { label: 'Preconditions', value: viewingTestCase.preconditions || '—' },
            { label: 'Steps', value: viewingTestCase.steps || '—' },
            { label: 'Expected Result', value: viewingTestCase.expected_result || '—' },
            { label: 'Created By', value: viewingTestCase.created_by },
            { label: 'Created At', value: new Date(viewingTestCase.created_at).toLocaleString() },
          ]}
          tags={viewingTestCase.tags}
          onClose={() => setViewingTestCase(null)}
        />
      )}

      {showBulkEditModal && (
        <BulkEditModal
          count={selectedTestCases.size}
          type="test_case"
          onApply={handleBulkEditTestCases}
          onCancel={() => setShowBulkEditModal(false)}
        />
      )}
    </div>
  );
};
